import { useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, Copy, Plus, Trash2, FileText, Wand2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUS_META = {
  ok: { label: "OK", cls: "bg-emerald-50 text-emerald-700", Icon: CheckCircle2 },
  attention: { label: "À vérifier", cls: "bg-amber-50 text-amber-700", Icon: AlertTriangle },
  doublon: { label: "Doublon", cls: "bg-rose-50 text-rose-700", Icon: Copy },
  incomplet: { label: "Incomplet", cls: "bg-rose-50 text-rose-700", Icon: AlertTriangle },
};

const GUARDIAN_KEYS = ["guardian_last_name", "guardian_first_name", "guardian_email", "guardian2_last_name", "guardian2_first_name", "guardian2_email"];
const emptyRow = () => ({ first_name: "", last_name: "", ...Object.fromEntries(GUARDIAN_KEYS.map((k) => [k, ""])), status: "ok", uncertain_fields: [], suggestions: {}, verified: {} });

export function ImportStudentsDialog({ open, onOpenChange, classId, onImported, onRequestPrint }) {
  const [step, setStep] = useState("upload");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [rows, setRows] = useState([]);
  const [created, setCreated] = useState([]);
  const [skipped, setSkipped] = useState(0);
  const fileRef = useRef(null);

  const reset = () => { setStep("upload"); setRows([]); setCreated([]); setSkipped(0); setSessionId(null); setLoading(false); setProgress(""); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const handleFile = async (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!name.endsWith(".xlsx") && !name.endsWith(".csv") && !name.endsWith(".pdf")) {
      toast.error("Formats acceptés : .xlsx, .csv ou .pdf");
      return;
    }
    if (file.size > 10 * 1024 * 1024) { toast.error("Fichier trop volumineux (maximum 10 Mo)."); return; }
    setLoading(true);
    const isPdf = name.endsWith(".pdf");
    if (isPdf) {
      setProgress("Analyse de votre liste…");
      setTimeout(() => setLoading((l) => { if (l) setProgress("Identification des élèves…"); return l; }), 1500);
      setTimeout(() => setLoading((l) => { if (l) setProgress("Vérification des données…"); return l; }), 4000);
    } else {
      setProgress("Analyse du fichier…");
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/classes/${classId}/students/import/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSessionId(data.session_id);
      setRows(data.rows.map((r) => ({ ...r, uncertain_fields: r.uncertain_fields || [], suggestions: r.suggestions || {}, verified: {} })));
      setStep("preview");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
      setProgress("");
    }
  };

  // Un champ est signalé douteux tant qu'il n'a pas été vérifié/corrigé.
  const isFlagged = (r, field) => (r.uncertain_fields || []).includes(field) && !r.verified?.[field];

  const updateRow = (i, field, value) =>
    setRows((prev) => prev.map((r, j) => {
      if (j !== i) return r;
      // Toute modification manuelle d'un champ douteux vaut vérification.
      const verified = (r.uncertain_fields || []).includes(field) ? { ...r.verified, [field]: true } : r.verified;
      return { ...r, [field]: value, verified };
    }));

  const acceptSuggestion = (i, field) =>
    setRows((prev) => prev.map((r, j) => {
      if (j !== i) return r;
      const sug = (r.suggestions || {})[field];
      if (!sug) return r;
      return { ...r, [field]: sug, verified: { ...r.verified, [field]: true } };
    }));

  const verifyRow = (i) =>
    setRows((prev) => prev.map((r, j) => {
      if (j !== i) return r;
      const verified = { ...r.verified };
      (r.uncertain_fields || []).forEach((f) => { verified[f] = true; });
      return { ...r, verified };
    }));

  const verifyAll = () =>
    setRows((prev) => prev.map((r) => {
      const verified = { ...r.verified };
      (r.uncertain_fields || []).forEach((f) => { verified[f] = true; });
      return { ...r, verified };
    }));

  const removeRow = (i) => setRows((prev) => prev.filter((_, j) => j !== i));
  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const flaggedCount = rows.reduce(
    (acc, r) => acc + (r.uncertain_fields || []).filter((f) => !r.verified?.[f]).length, 0
  );
  const validCount = rows.filter((r) => (r.first_name || "").trim() && (r.last_name || "").trim() && r.status !== "doublon").length;

  const confirmImport = async () => {
    setLoading(true);
    try {
      const payloadRows = rows
        .filter((r) => (r.first_name || "").trim() && (r.last_name || "").trim() && r.status !== "doublon")
        .map((r) => ({
          first_name: r.first_name, last_name: r.last_name,
          ...Object.fromEntries(GUARDIAN_KEYS.map((k) => [k, r[k] || ""])),
        }));
      const { data } = await api.post(`/classes/${classId}/students/import/confirm`, {
        session_id: sessionId, rows: payloadRows,
      });
      setCreated(data.created);
      setSkipped(data.skipped || 0);
      setStep("done");
      onImported?.();
      toast.success(`${data.count} élève(s) importé(s).`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const renderNameCell = (i, r, field, placeholder) => {
    const flagged = isFlagged(r, field);
    const sug = flagged ? (r.suggestions || {})[field] : null;
    return (
      <div className="space-y-1">
        <div className="relative">
          <Input
            value={r[field] || ""}
            onChange={(e) => updateRow(i, field, e.target.value)}
            placeholder={placeholder}
            className={`h-8 ${flagged ? "border-amber-400 bg-amber-50 pr-7 focus-visible:ring-amber-400 dark:bg-amber-500/10" : ""}`}
            data-testid={`row-${field === "last_name" ? "lastname" : "firstname"}-${i}`}
          />
          {flagged && (
            <AlertTriangle
              size={13}
              className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-amber-500"
              data-testid={`flag-${field}-${i}`}
            />
          )}
        </div>
        {sug && (
          <button
            type="button"
            onClick={() => acceptSuggestion(i, field)}
            className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 hover:bg-blue-100 dark:bg-blue-500/10 dark:text-blue-300"
            title="Appliquer la correction proposée"
            data-testid={`suggest-${field}-${i}`}
          >
            <Wand2 size={11} /> {sug}
          </button>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[90vh] overflow-hidden rounded-2xl sm:max-w-[960px]">
        <DialogHeader>
          <DialogTitle>Importer une liste d'élèves</DialogTitle>
          <DialogDescription>
            Formats acceptés : Excel (.xlsx), CSV (.csv) ou PDF (.pdf) — max 10 Mo. Un PDF texte ou scanné est analysé automatiquement.
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="py-4">
            <button
              type="button"
              onClick={() => !loading && fileRef.current?.click()}
              className="flex w-full flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/50 py-12 transition-colors hover:border-slate-300 dark:border-white/10 dark:bg-white/[0.02]"
              data-testid="import-dropzone"
            >
              {loading ? <Loader2 className="animate-spin text-slate-400" size={28} /> : <Upload className="text-slate-400" size={28} />}
              <span className="text-sm font-medium text-slate-600 dark:text-slate-300" data-testid="import-progress">
                {loading ? (progress || "Analyse…") : "Cliquez pour choisir un fichier (.xlsx, .csv ou .pdf)"}
              </span>
              {!loading && <span className="text-xs text-slate-400">La liste sera vérifiable avant création.</span>}
            </button>
            <input
              ref={fileRef} type="file" accept=".xlsx,.csv,.pdf" className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="import-file-input"
            />
          </div>
        )}

        {step === "preview" && (
          <div className="flex flex-col gap-3">
            {rows.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid="import-empty">
                <FileText className="text-slate-300" size={36} />
                <p className="text-sm text-slate-500">Nous n'avons pas pu identifier d'élèves. Réessayez avec un autre fichier ou ajoutez-les manuellement.</p>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2" data-testid="import-summary">
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">{rows.length} détecté(s)</span>
                  <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">{validCount} à créer</span>
                  {flaggedCount > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700" data-testid="import-flagged-count">
                      <AlertTriangle size={12} /> {flaggedCount} nom(s) à vérifier
                    </span>
                  )}
                  {flaggedCount > 0 && (
                    <button
                      onClick={verifyAll}
                      className="inline-flex items-center gap-1 rounded-full border border-amber-200 px-3 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50"
                      data-testid="verify-all"
                    >
                      <ShieldCheck size={12} /> Tout marquer comme vérifié
                    </button>
                  )}
                  <span className="ml-auto text-xs text-slate-400">Corrigez si besoin, puis confirmez.</span>
                </div>
                <div className="max-h-[45vh] overflow-auto rounded-xl border border-slate-100 dark:border-white/10">
                  <table className="w-full min-w-[900px] text-sm">
                    <thead className="sticky top-0 z-10 bg-slate-50 text-left text-xs text-slate-400 dark:bg-[#0f0f0f]">
                      <tr>
                        <th className="px-2 py-2">Nom</th><th className="px-2 py-2">Prénom</th>
                        <th className="px-2 py-2">Resp. 1 (nom / prénom)</th><th className="px-2 py-2">E-mail 1</th>
                        <th className="px-2 py-2">Resp. 2 (nom / prénom)</th><th className="px-2 py-2">E-mail 2</th>
                        <th className="px-2 py-2">Statut</th><th className="px-2 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => {
                        const m = STATUS_META[r.status] || STATUS_META.ok;
                        const rowFlagged = isFlagged(r, "first_name") || isFlagged(r, "last_name");
                        return (
                          <tr key={i} className={`border-t border-slate-50 dark:border-white/5 ${rowFlagged ? "bg-amber-50/40 dark:bg-amber-500/5" : ""}`} data-testid={`preview-row-${i}`}>
                            <td className="px-2 py-1.5 align-top">{renderNameCell(i, r, "last_name")}</td>
                            <td className="px-2 py-1.5 align-top">{renderNameCell(i, r, "first_name")}</td>
                            <td className="px-2 py-1.5 align-top">
                              <div className="flex gap-1">
                                <Input value={r.guardian_last_name || ""} onChange={(e) => updateRow(i, "guardian_last_name", e.target.value)} className="h-8 w-24" placeholder="Nom" data-testid={`row-g1-last-${i}`} />
                                <Input value={r.guardian_first_name || ""} onChange={(e) => updateRow(i, "guardian_first_name", e.target.value)} className="h-8 w-24" placeholder="Prénom" data-testid={`row-g1-first-${i}`} />
                              </div>
                            </td>
                            <td className="px-2 py-1.5 align-top"><Input value={r.guardian_email || ""} onChange={(e) => updateRow(i, "guardian_email", e.target.value)} className="h-8 w-36" placeholder="—" data-testid={`row-g1-email-${i}`} /></td>
                            <td className="px-2 py-1.5 align-top">
                              <div className="flex gap-1">
                                <Input value={r.guardian2_last_name || ""} onChange={(e) => updateRow(i, "guardian2_last_name", e.target.value)} className="h-8 w-24" placeholder="Nom" data-testid={`row-g2-last-${i}`} />
                                <Input value={r.guardian2_first_name || ""} onChange={(e) => updateRow(i, "guardian2_first_name", e.target.value)} className="h-8 w-24" placeholder="Prénom" data-testid={`row-g2-first-${i}`} />
                              </div>
                            </td>
                            <td className="px-2 py-1.5 align-top"><Input value={r.guardian2_email || ""} onChange={(e) => updateRow(i, "guardian2_email", e.target.value)} className="h-8 w-36" placeholder="—" data-testid={`row-g2-email-${i}`} /></td>
                            <td className="px-2 py-1.5 align-top">
                              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${m.cls}`} title={(r.issues || []).join(", ")}>
                                <m.Icon size={12} /> {m.label}
                              </span>
                            </td>
                            <td className="px-2 py-1.5 align-top">
                              <div className="flex items-center gap-1">
                                {rowFlagged && (
                                  <button onClick={() => verifyRow(i)} className="rounded p-1 text-amber-500 hover:text-emerald-600" title="Marquer cette ligne comme vérifiée" data-testid={`verify-row-${i}`} aria-label="Marquer comme vérifié"><ShieldCheck size={15} /></button>
                                )}
                                <button onClick={() => removeRow(i)} className="rounded p-1 text-slate-400 hover:text-rose-600" data-testid={`remove-row-${i}`} aria-label="Supprimer la ligne"><Trash2 size={15} /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <button onClick={addRow} className="flex items-center gap-1.5 self-start text-sm font-medium text-blue-700 hover:text-blue-900 dark:text-blue-400" data-testid="add-row"><Plus size={15} /> Ajouter un élève</button>
              </>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-xl" onClick={() => setStep("upload")} data-testid="import-back">Changer de fichier</Button>
              <Button className="rounded-xl" disabled={loading || validCount === 0} onClick={confirmImport} data-testid="import-confirm">
                {loading ? <Loader2 size={18} className="animate-spin" /> : `Confirmer l'import (${validCount})`}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "done" && (
          <div className="flex flex-col items-center gap-4 py-6 text-center" data-testid="import-done">
            <CheckCircle2 className="text-emerald-500" size={40} />
            <div>
              <p className="font-medium text-slate-800 dark:text-slate-100">✓ {created.length} élève(s) importé(s)</p>
              {skipped > 0 && <p className="mt-1 text-sm text-amber-600">⚠️ {skipped} doublon(s) ignoré(s)</p>}
              <p className="mt-1 text-sm text-slate-500">Les comptes parents ont été créés automatiquement.</p>
            </div>
            <div className="flex gap-2">
              <Button className="rounded-xl" onClick={() => onRequestPrint?.(created)} data-testid="open-print-credentials">
                <FileSpreadsheet size={16} className="mr-2" /> Imprimer les identifiants
              </Button>
              <Button variant="outline" className="rounded-xl" onClick={() => close(false)}>Terminer</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
